import React, { useMemo } from 'react';
import styles from '../OrderList.module.scss';
import clsx from 'clsx';
import { Table } from '../../warranty-request/components/Table.tsx';
import Chevron from 'assets/icons/chevron.svg?react';
import { useScopedTranslation } from '../../../../application/localization/useScopedTranslation.ts';
import { IColumnProps } from 'devextreme-react/data-grid';
import formStyles from '../../../../components/form/form.module.scss';
import { formatDate } from '../../../../helpers/abas-helper.ts';
import { PaginationProps } from '../OrderList.tsx';
import { isEqual } from 'date-fns';
import { OrderListRecord } from '../../../../services/api/api-client.types.ts';

type OwnProps = {
  data: OrderListRecord[];
  openedOrders: Set<string>;
  isDesc: boolean;
  paginationProps: PaginationProps;
  handleClickAccordion: (key: string) => void;
};

const getDataRange = (value: OrderListRecord) => {
  if (!value.earliestDeliveryDate || !value.latestDeliveryDate) {
    return '';
  }
  if (isEqual(value.earliestDeliveryDate, value.latestDeliveryDate)) {
    return formatDate(value.earliestDeliveryDate);
  } else {
    return `${formatDate(value.earliestDeliveryDate)}-${formatDate(
      value.latestDeliveryDate,
    )}`;
  }
};

export const Accordion = (props: OwnProps) => {
  const { data, openedOrders, isDesc, paginationProps, handleClickAccordion } =
    props;

  const i18n = useScopedTranslation('Page.OrderList');
  const columnsConfig = useMemo(() => getColumnsConfig(i18n), []);

  const outerTableTextStyles = [styles.text, styles.overflowEllipsis];

  return (
    <div className={styles.marginBottom65}>
      {data.map((item, index) => (
        <div data-test-id={'group-container'} key={item.order}>
          <div
            className={clsx(
              styles.rowContainer,
              !openedOrders.has(item.order) &&
                index !== data.length - 1 &&
                styles.borderBottom,
            )}
          >
            <Chevron
              data-test-id={'expand'}
              className={clsx(
                styles.pointer,
                !openedOrders.has(item.order) && styles.rotate180,
              )}
              onClick={() => handleClickAccordion(item.order)}
            />
            <div
              data-test-id={'group-order-number'}
              className={clsx(outerTableTextStyles, styles.width132)}
            >
              {item.order}
            </div>
            <div
              data-test-id={'group-from-date'}
              className={clsx(outerTableTextStyles, styles.width124)}
            >
              {formatDate(item.from ?? '')}
            </div>
            <div className={clsx(outerTableTextStyles, styles.flex1)}>
              {item.yourOrder}
            </div>
            <div
              data-test-id={'group-reference'}
              className={clsx(outerTableTextStyles, styles.flex1)}
            >
              {item.yourReference}
            </div>
            <div
              data-test-id={'group-delivery-date'}
              className={clsx(outerTableTextStyles, styles.width224)}
            >
              {getDataRange(item)}
            </div>
            <div
              data-test-id={'sum-quantity'}
              className={clsx(outerTableTextStyles, styles.width112)}
            >
              {item.quantity}
            </div>
          </div>
          {openedOrders.has(item.order) && (
            <div className={clsx(styles.paddingLeft20, styles.fix)}>
              <Table
                data={item.innerTable}
                columnsConfig={columnsConfig}
                paginationProps={paginationProps}
                desc={isDesc}
                customTableClassname={styles.hideCollapseAllRow}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const getColumnsConfig = (
  i18n: any,
): (IColumnProps & { disableExport?: boolean })[] => [
  {
    dataField: 'article',
    allowEditing: false,
    caption: i18n.t('article'),
    cssClass: clsx(formStyles.textCellPadding),
    width: 132,
    sortingMethod: (a, b) => +a - +b,
  },
  {
    dataField: 'name',
    allowEditing: false,
    caption: i18n.t('name'),
    cssClass: formStyles.textCellPadding,
    minWidth: 200,
  },
  {
    dataField: 'deliveryDate',
    allowEditing: false,
    caption: i18n.t('delivery_date_earliest'),
    cssClass: formStyles.textCellPadding,
    width: 224,
    cellRender: (cell) => formatDate(cell.value),
    sortOrder: 'asc',
  },
  {
    dataField: 'quantity',
    allowEditing: false,
    caption: i18n.t('quantity'),
    cssClass: formStyles.textCellPadding,
    width: 112,
    sortingMethod: (a, b) => +a - +b,
  },
];
